import { useCustomDispatch, useCustomSelector } from '@/core/redux/hooks';
import { useEffect, useRef } from 'react';
import { setCounter } from '@/core/redux/slices';

export const useAnimatedCounter = (
	startValue: number,
	endValue: number,
	duration: number,
	trigger: boolean // New parameter to trigger animation
): number => {
	/*********************
	// #region Hooks
	**********************/
	const dispatch = useCustomDispatch();

	/*********************
	// #region Redux
	**********************/
	const { counter } = useCustomSelector((state) => state.counter);
	const requestRef = useRef<number | null>(null);
	const startTimestamp = useRef<number | null>(null);
	const animationTriggered = useRef<boolean>(false); // Track if animation is triggered

	const animate = (timestamp: number) => {
		if (!startTimestamp.current) startTimestamp.current = timestamp;
		const progress = timestamp - startTimestamp.current;
		const progressRatio = Math.min(progress / duration, 1);
		const increment = progressRatio * (endValue - startValue);

		dispatch(setCounter(startValue + increment));

		if (progress < duration) {
			requestRef.current = requestAnimationFrame(animate);
		} else {
			// Ensure the final value is set correctly
			dispatch(setCounter(endValue));
			cancelAnimationFrame(requestRef.current!);
			startTimestamp.current = null; // Reset startTimestamp after animation ends
			animationTriggered.current = false;
		}
	};

	useEffect(() => {
		if (trigger && !animationTriggered.current) {
			animationTriggered.current = true;
			startTimestamp.current = null; // Reset startTimestamp
			requestRef.current = requestAnimationFrame(animate);
		} else if (!trigger && animationTriggered.current) {
			// Reset animation if trigger is false
			animationTriggered.current = false;
			cancelAnimationFrame(requestRef.current!);
		}

		return () => {
			cancelAnimationFrame(requestRef.current!);
			animationTriggered.current = false;
		};
	}, [trigger, startValue, endValue, duration]);

	return counter;
};
